import { AntiForgeryToken, AuthenticationError, OAuth2Client, TokenInformation } from '@kolorado/oauth';
import qs from 'qs';
import { loginActionNames } from '../../store/login/types';
import { AppThunkDispatch } from '../../store';

const AUTH_TOKENS_UPDATED_EVENT = 'auth:tokens-updated';

const clearOAuthQueryParameters = () => {
  try {
    const url = new URL(window.location.href);
    const keys = ['code', 'state', 'session_state', 'iss'];
    keys.forEach((key) => url.searchParams.delete(key));
    const search = url.searchParams.toString();
    const next = `${url.pathname}${search ? `?${search}` : ''}${url.hash}`;
    window.history.replaceState({}, document.title, next);
  } catch {
    // Ignore URL parsing issues (older browsers/tests).
  }
};

const isInvalidGrant = (error: any): boolean => {
  const errorCode = error?.response?.data?.error ?? error?.error;
  const description = String(error?.response?.data?.error_description ?? error?.message ?? '').toLowerCase();
  return String(errorCode ?? '').toLowerCase() === 'invalid_grant' || description.includes('authorization code is invalid');
};

const Authentication = (search: string, client: OAuth2Client, dispatch: AppThunkDispatch) => {
  const searchObject = qs.parse(search, { delimiter: /[&?]/ });
  const { code, state } = searchObject;
  if (code === undefined) {
    client.authenticate({ clientToken: 'CustomerMasterUI_ac_client' });
    return;
  }

  if (!state) {
    dispatch({ type: loginActionNames.SET_LOGIN_ERROR, payload: 'Missing anti-forgery state parameter' });
    clearOAuthQueryParameters();
    return;
  }

  const codeValue = String(code);
  const stateValue = String(state);
  const handledKey = `auth:code-exchange:${codeValue}:${stateValue}`;
  try {
    if (sessionStorage.getItem(handledKey)) {
      clearOAuthQueryParameters();
      return;
    }
  } catch {
    // sessionStorage may be unavailable; continue without the guard.
  }

  if (state) {
    const antiForgeryToken = state as string;
    const { clientToken, synk } = AntiForgeryToken.parse(antiForgeryToken);
    try {
      client.validateAntiForgeryToken({ antiForgeryToken, clientToken, synk });
      if (code) {
        try {
          sessionStorage.setItem(handledKey, '1');
        } catch {
          // sessionStorage may be unavailable; continue without the guard.
        }

        clearOAuthQueryParameters();
        client
          .authorize(code as string, { synk })
          .then(async (tokenInformation: TokenInformation) => {
            const { token, refreshToken } = tokenInformation;
            localStorage.setItem('accessToken', token);
            if (refreshToken) {
              localStorage.setItem('refreshToken', refreshToken);
            }
            window.dispatchEvent(new Event(AUTH_TOKENS_UPDATED_EVENT));
            return dispatch({ type: loginActionNames.SET_CLIENT, payload: client });
          })
          .catch((error) => {
            dispatch({ type: loginActionNames.SET_LOGIN_ERROR, payload: error });
            if (isInvalidGrant(error)) {
              try {
                sessionStorage.removeItem(handledKey);
              } catch {
                // ignore
              }
              client.authenticate({ clientToken: 'CustomerMasterUI_ac_client' });
            }
          });
      }
    } catch (error) {
      let message = 'Failure in anti-forgery validation';
      if (error instanceof AuthenticationError) {
        const authError: AuthenticationError = error as AuthenticationError;
        message = authError.message;
      }
      dispatch({ type: loginActionNames.SET_LOGIN_ERROR, payload: message });
      clearOAuthQueryParameters();
      client.authenticate({ clientToken: 'CustomerMasterUI_ac_client' });
    }
  }
};
export default Authentication;
